import * as React from "react";
import { styled } from "@mui/material/styles";
import {
  Box,
  Button,
  Chip,
  Divider,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import {
  KeyBooleanValue,
  KeyValue,
  Query,
  TableHeader,
} from "../../../../interfaces";
import {
  primaryHeaderColor,
  textHeaderColor,
  additionalHeaderColor,
  secondaryHeaderColor,
} from "../../../../refdata/constants";
import MultiHeaderTable from "../../../MultiHeaderTable/MultiHeaderTable";
import { fetchData } from "../../../../common/services/ApiClient";
import { ApiBaseUrl } from "../../../../config";
import {
  fileNameFormat,
  reduceObj,
  sortObjValue,
  sumValueArrayObj,
} from "../../../../common/services/Utilities";
import MarketShareCountyList from "../MarketShareCountyList";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import clsx from "clsx";
import ApiDataDownload, { apiDownloadProps } from "../../../ApiDataDownload";
import { appName } from "./MarketPresenceTables";
import LoadingIndicator from "../../../LoadingIndicator";
import ModalDialog from "../../../ModalDialog";
import BranchOfficeDeposits from "../../../../containers/BranchOfficeDeposits";
import {
  selectedStates,
  sodStatesArray,
} from "../../../../refdata/FilterOptions";

const PREFIX = "MarketShareStateList";

const classes = {
  chip: `${PREFIX}-chip`,
  openCloseAll: `${PREFIX}-openCloseAll`,
  footer: `${PREFIX}-footer`,
  groupHover: `${PREFIX}-groupHover`,
  group: `${PREFIX}-group`,
  rowIndent: `${PREFIX}-rowIndent`,
  staticRow: `${PREFIX}-staticRow`,
  tableRow: `${PREFIX}-tableRow`,
  tableRowHover: `${PREFIX}-tableRowHover`,
  singleState: `${PREFIX}-singleState`,
  otherStates: `${PREFIX}-otherStates`,
  moreInfo: `${PREFIX}-moreInfo`,
};

const Root = styled("div")(({ theme }) => ({
  "& #multi-header-table-head": {
    "position": "sticky",
    "top": 0,
    "& tr": {
      position: "static",
      borderCollaps: "collapse",
    },
    "& th": {
      position: "static",
      borderCollaps: "collapse",
    },
  },
  [`& .${classes.chip}`]: {
    "cursor": "pointer",
    "backgroundColor": "transparent",
    "color": theme.palette.primary.main,
    "fontSize": 14,
    "float": "left",
    "& .MuiChip-icon": {
      color: theme.palette.primary.main,
    },
  },

  [`& .${classes.openCloseAll}`]: {
    "minWidth": 80,
    "margin": 0,
    "color": "#ffffff",
    "fontWeight": 600,
    "backgroundColor": "#426cac",
    "&:hover, &.Mui-focusVisible": {
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.secondary.main,
    },
  },

  [`& .${classes.footer}`]: {
    "& td": {
      "fontWeight": "bold",
      "&first-child": { paddingLeft: 20 },
    },
  },

  [`& .${classes.groupHover}`]: {
    "&.MuiTableCell-root.MuiTableCell-body": {
      backgroundColor: "#CED6E3",
      textDecoration: "underline",
      cursor: "pointer",
    },
  },

  [`& .${classes.group}`]: {
    textDecoration: "underline",
  },

  [`& .${classes.rowIndent}`]: {
    "&.MuiTableCell-root.MuiTableCell-body": {
      textAlign: "left",
    },
    "fontWeight": "bold",
    "textIndent": "20px",
    "textWrap": "nowrap",
    "textAlign": "left",
  },

  [`& .${classes.staticRow}`]: {
    "cursor": "default",
    "& td": {
      "backgroundColor": "#acc7df",
      "&:first-child": {
        paddingLeft: 42,
      },
    },
  },

  [`& .${classes.tableRow}`]: {
    "cursor": "pointer",
    "& td": {
      backgroundColor: "#acc7df",
      fontWeight: "bold",
    },
  },

  [`& .${classes.tableRowHover}`]: {
    "cursor": "pointer",
    "& td": {
      backgroundColor: "#acc7df",
      fontWeight: "bold",
    },
    "&:hover td, &:focus-visible td": {
      backgroundColor: "#CDD6E3",
    },
  },

  [`& .${classes.singleState}`]: {
    "& td": {
      "&:first-child": {
        paddingLeft: 8,
      },
    },
  },

  [`& .${classes.otherStates}`]: {
    "& td": {
      "backgroundColor": "white",
      "&:first-child": {
        paddingLeft: 8,
      },
    },
  },

  [`& .${classes.moreInfo}`]: {
    "color": "#0000ff",
    "fontWeight": "bold",
    "fontSize": 16,
    "lineHeight": "24px",
    "paddingBottom": "10px",
    "fontStyle": "normal",
    "&:hover, &.Mui-focusVisible": {
      textDecoration: "underline",
      cursor: "pointer",
    },
  },
}));

interface Props {
  appState: Query;
  searchPills: React.ReactElement;
  updateUi: (key: string, value: string | KeyValue[]) => void;
  updateFilters: (key: string, value: string | number) => void;
  updateSearch: (key: string, value: string) => void;
}

export default function MarketShareStateList({
  appState,
  updateUi,
  updateFilters,
  updateSearch,
  searchPills,
}: Props) {
  const [openStates, setOpenStates] = React.useState<KeyBooleanValue>({});

  const [tableData, setTableData] = React.useState<KeyValue[]>([]);

  const [footerData, setFooterData] = React.useState<KeyValue>({});
  const dataExistsCheck = appState.filters.stalpbr
    ? tableData.length > 0
    : tableData.length > 1;
  const singleState =
    appState.filters.stalpbr && appState.filters.stalpbr !== "ALL"
      ? appState.filters.stalpbr
      : "";
  const [loading, setLoading] = React.useState(true);
  const [prevInstType, setPrevInstType] = React.useState(
    appState.ui.institutionType,
  );

  const handleScrollToBottom = () => {
    window.scrollTo({
      top: document.body.scrollHeight,
      behavior: "smooth",
    });
  };

  const moreInformation = appState.ui.institutionType === "bhcs" && (
    <>
      <Typography className={classes.moreInfo} onClick={handleScrollToBottom}>
        Click for important information about this data.
      </Typography>
    </>
  );

  React.useEffect(() => {
    if (
      appState.ui.institutionType === "banks" &&
      prevInstType !== appState.ui.institutionType
    ) {
      setPrevInstType(appState.ui.institutionType);
      updateSearch("bhcName", "");
      updateFilters("bhcRSSD", "");
    }
    if (
      appState.ui.institutionType === "bhcs" &&
      prevInstType !== appState.ui.institutionType
    ) {
      setPrevInstType(appState.ui.institutionType);
      updateSearch("name", "");
      updateFilters("cert", "");
    }
  }, [appState.ui.institutionType, prevInstType, updateFilters, updateSearch]);

  React.useEffect(() => {
    if (
      prevInstType === appState.ui.institutionType &&
      appState.filters.lastYear &&
      (appState.filters.cert || appState.filters.bhcRSSD) &&
      appState.ui.reportType === "marketPresence"
    ) {
      setLoading(true);
      const singleStateFilter = singleState
        ? ` AND STALPBR:"${singleState}"`
        : "";

      const nameOrNameHCR = appState.filters.cert ? "NAMEFULL" : "NAMEHCR";

      const certRSSDFilter = appState.filters.cert
        ? `CERT:${appState.filters.cert}`
        : `RSSDHCR:${appState.filters.bhcRSSD}`;

      let isMounted = true;
      (async () => {
        await Promise.all([
          fetchData(
            `${ApiBaseUrl()}/sod?filters=YEAR:${
              appState.filters.lastYear
            }${singleStateFilter}&agg_by=STNUMBR&agg_sum_fields=DEPSUMBR`,
          ),
          fetchData(
            `${ApiBaseUrl()}/sod?filters=YEAR:${
              appState.filters.lastYear
            } AND ${certRSSDFilter}${singleStateFilter}&agg_by=STNUMBR&agg_sum_fields=DEPSUMBR`,
          ),
          fetchData(
            `${ApiBaseUrl()}/sod?filters=YEAR:${
              appState.filters.lastYear
            }${singleStateFilter}&agg_by=STCNTYBR&agg_sum_fields=DEPSUMBR&agg_limit=10000&sort_by=STCNTYBR&sort_order=ASC`,
          ),
          fetchData(
            `${ApiBaseUrl()}/sod?filters=YEAR:${
              appState.filters.lastYear
            } AND ${certRSSDFilter}${singleStateFilter}&agg_by=STCNTYBR&agg_sum_fields=DEPSUMBR&agg_limit=10000&sort_by=STCNTYBR&sort_order=ASC`,
          ),
          fetchData(
            `${ApiBaseUrl()}/sod?filters=YEAR:${
              appState.filters.lastYear
            }${singleStateFilter}&get_unique=STCNTYBR&fields=STCNTYBR,CNTYNAMB&limit=10000`,
          ),
          fetchData(
            `${ApiBaseUrl()}/sod?filters=YEAR:${
              appState.filters.lastYear
            } AND ${certRSSDFilter}${
              appState.filters.cert ? ` AND BKMO:1` : `&get_unique=RSSDHCR`
            }&fields=${nameOrNameHCR}`,
          ),
        ])
          .then((response) => {
            if (isMounted) {
              const allStateData: KeyValue[] = response[0].data;
              const allStateTotals = response[0].totals;
              const marketStateData: KeyValue[] = response[1].data;
              const marketStateTotals = response[1].totals;
              const allCountyData: KeyValue[] = response[2].data;
              const marketCountyData: KeyValue[] = response[3].data;

              updateUi("institutionName", response[5]?.data[0][nameOrNameHCR]);

              const currentMarketStates = marketStateData.map(
                (market: KeyValue) => market.STNUMBR,
              );
              const marketStateNames: string[] = [];
              let combinedData: KeyValue[] = allStateData.map((data) => {
                const stateName =
                  selectedStates.find(
                    (state: KeyValue) => state.value === data.STNUMBR,
                  )?.label || data.STNUMBR;

                const matchingData = marketStateData.find(
                  (market) => market.STNUMBR === data.STNUMBR,
                );

                if (matchingData) {
                  marketStateNames.push(stateName);
                }
                //matches on the first numbers of the state and also confirms the length matches state + county number
                const filteredCounties = allCountyData.filter(
                  (allCounty) =>
                    allCounty.STCNTYBR?.toString().startsWith(data.STNUMBR) &&
                    allCounty.STCNTYBR?.toString().length ===
                      data.STNUMBR?.toString().length + 3,
                );

                let counties: KeyValue[] = filteredCounties.map((county) => {
                  const market = marketCountyData.find(
                    (marketCounty) => marketCounty.STCNTYBR === county.STCNTYBR,
                  );

                  const countyName =
                    response[4]?.data.find(
                      (res: KeyValue) => res.STCNTYBR === county.STCNTYBR,
                    )?.CNTYNAMB ?? county.STCNTYBR;
                  return {
                    CNTYNAMB: countyName,
                    STCNTYBR: county.STCNTYBR,
                    count: market?.count ?? "-",
                    sum_DEPSUMBR: market?.sum_DEPSUMBR ?? "-",
                    all_count: county.count,
                    all_sum: county.sum_DEPSUMBR,
                    mkt: market
                      ? `${(
                          (market.sum_DEPSUMBR / data.sum_DEPSUMBR) *
                          100
                        ).toFixed(2)}%`
                      : "0.00%",
                  };
                });
                sortObjValue(counties, "CNTYNAMB");
                if (!appState.ui.displayAll) {
                  const otherMarketCountiesByState = counties.filter(
                    (data) => data.count === "-",
                  );
                  const otherCounties: KeyValue = {
                    CNTYNAMB: "Other Counties",
                    all_count: sumValueArrayObj(
                      otherMarketCountiesByState,
                      "all_count",
                    ),
                    sum_DEPSUMBR: "-",
                    count: "-",
                    all_sum: sumValueArrayObj(
                      otherMarketCountiesByState,
                      "all_sum",
                    ),
                    mkt: "-",
                  };
                  counties = counties.filter((data) => data.count !== "-");
                  counties.push(otherCounties);
                }

                return {
                  STNAMEBR: stateName,
                  STALPBR: data.STNUMBR,
                  count: matchingData?.count ?? "-",
                  sum_DEPSUMBR: matchingData?.sum_DEPSUMBR ?? "-",
                  all_count: data.count,
                  counties: counties,
                  all_sum: data.sum_DEPSUMBR,
                  mkt: matchingData
                    ? `${(
                        (matchingData.sum_DEPSUMBR / data.sum_DEPSUMBR) *
                        100
                      ).toFixed(2)}%`
                    : "0.00%",
                };
              });

              if (!appState.ui.displayAll) {
                combinedData = combinedData.filter(
                  (data) => data.count !== "-",
                );
                if (!appState.filters.stalpbr) {
                  const otherMarketStates = allStateData.filter(
                    (all: KeyValue) =>
                      !currentMarketStates.includes(all.STNUMBR),
                  );
                  //other states row when display all is not checked
                  const otherStates: KeyValue = {
                    STNAMEBR: "Other States",
                    all_count: sumValueArrayObj(otherMarketStates, "count"),
                    sum_DEPSUMBR: "-",
                    count: "-",
                    all_sum: sumValueArrayObj(
                      otherMarketStates,
                      "sum_DEPSUMBR",
                    ),
                    mkt: "-",
                  };
                  combinedData.push(otherStates);
                }
              }

              //sets all states in market to closed unless it's a single state filter
              setOpenStates(
                reduceObj(
                  marketStateNames.map((state: string) => {
                    return {
                      key: state,
                      value: !!singleStateFilter,
                    };
                  }),
                ),
              );

              //footer for results
              const resultTotals: KeyValue = {
                all_count: allStateTotals.count,
                sum_DEPSUMBR: marketStateTotals.sum_DEPSUMBR,
                count: marketStateTotals.count,
                all_sum: allStateTotals.sum_DEPSUMBR,
                mkt: `${(
                  (marketStateTotals.sum_DEPSUMBR /
                    allStateTotals.sum_DEPSUMBR) *
                  100
                ).toFixed(2)}%`,
              };
              setTableData(combinedData);
              setFooterData(resultTotals);
            }
          })
          .catch((error: Error) => {
            console.error(error);
          })
          .finally(() => {
            isMounted = false;
            setLoading(false);
          });
      })();
    }
  }, [
    appState.filters.bhcRSSD,
    appState.filters.cert,
    appState.filters.lastYear,
    appState.filters.stalpbr,
    appState.ui.displayAll,
    appState.ui.institutionType,
    appState.ui.reportType,
    prevInstType,
    singleState,
    updateUi,
  ]);

  const downloadCertOrRSSD = appState.filters.cert
    ? {
        "Institution Name": appState.ui.institutionName,
        "Cert #": appState.filters.cert,
      }
    : {
        "BHC Name": appState.ui.institutionName,
        "BHC ID": appState.filters.bhcRSSD,
      };
  const fipsCode = singleState ? { "FIPS Code": "-" } : {};
  //transforms data for download
  const formattedDataForDownload: any = tableData.length
    ? tableData.map((data: KeyValue) => {
        if (data.counties) {
          const countyResults = data.counties.map((countyData: KeyValue) => {
            const fipsCode =
              singleState && countyData
                ? { "FIPS Code": countyData.STCNTYBR }
                : {};
            return {
              "Reporting Period": `6/30/${appState.filters.lastYear}`,
              ...downloadCertOrRSSD,
              "State": data.STNAMEBR,
              "County": countyData.CNTYNAMB,
              ...fipsCode,
              "Market Office Count": countyData.count?.toLocaleString(),
              "Market Total Deposits (Thousands USD)":
                countyData.sum_DEPSUMBR?.toLocaleString(),
              "All Office Count": countyData.all_count?.toLocaleString(),
              "All Total Deposits (Thousands USD)":
                countyData.all_sum?.toLocaleString(),
              "Market Share": countyData.mkt,
            };
          });
          return [
            {
              "Reporting Period": `6/30/${appState.filters.lastYear}`,
              ...downloadCertOrRSSD,
              "State": data.STNAMEBR,
              "County": "-",
              ...fipsCode,
              "Market Office Count": data.count?.toLocaleString(),
              "Market Total Deposits (Thousands USD)":
                data.sum_DEPSUMBR?.toLocaleString(),
              "All Office Count": data.all_count?.toLocaleString(),
              "All Total Deposits (Thousands USD)":
                data.all_sum?.toLocaleString(),
              "Market Share": data.mkt,
            },
            ...countyResults,
          ];
        } else {
          return {
            "Reporting Period": `6/30/${appState.filters.lastYear}`,
            ...downloadCertOrRSSD,
            "State": data.STNAMEBR,
            "County": "-",
            ...fipsCode,
            "Market Office Count": data.count?.toLocaleString(),
            "Market Total Deposits (Thousands USD)":
              data.sum_DEPSUMBR?.toLocaleString(),
            "All Office Count": data.all_count?.toLocaleString(),
            "All Total Deposits (Thousands USD)":
              data.all_sum?.toLocaleString(),
            "Market Share": data.mkt,
          };
        }
      })
    : [{}];

  const [groupHover, setGroupHover] = React.useState("");
  const [open, setOpen] = React.useState("");
  const branchOfficeModal = (
    <ModalDialog
      title={appState.ui.institutionName}
      display={open !== ""}
      setDisplay={() => setOpen("")}
      maxWidth="lg"
    >
      <BranchOfficeDeposits
        providedAppState={{
          ...appState,
          filters: {
            ...appState.filters,
            stname:
              sodStatesArray.find(
                (state) => state.toLowerCase() === open.toLowerCase(),
              ) || open,
          },
        }}
      />
    </ModalDialog>
  );
  const table: JSX.Element[] = dataExistsCheck
    ? tableData.map((data: KeyValue, int: number) => {
        const openCloseState = (e: any) => {
          const enterKey = e.key ? e.key === "Enter" : true;
          Object.keys(openStates).includes(data.STNAMEBR) &&
            !(appState.filters.stalpbr && appState.filters.stalpbr !== "ALL"
              ? appState.filters.stalpbr
              : "") &&
            enterKey &&
            setOpenStates({
              ...openStates,
              [data.STNAMEBR]: !openStates[data.STNAMEBR],
            });
        };
        const nonMarketData = data.sum_DEPSUMBR === "-";
        const otherStates = data.STNAMEBR === "Other States";
        const singleStateStyles = singleState ? classes.singleState : "";
        const otherDataStyles = otherStates
          ? classes.otherStates
          : singleStateStyles;

        const tableCellProps = (stname: string) => {
          if (nonMarketData) {
            return;
          }

          const clickThroughFn = (e: any) => {
            if (e.key && e.key !== "Enter") {
              return;
            }
            if (
              (appState.filters.bhcRSSD || appState.filters.cert) &&
              appState.filters.lastYear
            ) {
              setOpen(stname);
            }
          };
          return {
            onMouseEnter: () => setGroupHover(stname),
            onMouseLeave: () => setGroupHover(""),
            onFocus: () => setGroupHover(stname),
            onBlur: () => setGroupHover(""),
            onClick: clickThroughFn,
            onKeyDown: clickThroughFn,
            tabIndex: 0,
            className: clsx(
              groupHover === stname ? classes.groupHover : classes.group,
            ),
          };
        };
        const stateName =
          sodStatesArray.find(
            (state) => state.toLowerCase() === data.STNAMEBR?.toLowerCase(),
          ) || data.STNAMEBR;
        return (
          <React.Fragment key={`${data.STNAMEBR}_${int}`}>
            <TableRow
              id={data.STNAMEBR}
              onKeyDown={groupHover ? undefined : (e: any) => openCloseState(e)}
              className={
                nonMarketData || singleState
                  ? clsx(classes.staticRow, otherDataStyles)
                  : groupHover
                    ? classes.tableRow
                    : classes.tableRowHover
              }
              tabIndex={nonMarketData || otherStates ? undefined : 0}
            >
              <TableCell className={classes.rowIndent} onClick={openCloseState}>
                {nonMarketData || otherStates || singleState ? (
                  stateName
                ) : (
                  <Chip
                    className={classes.chip}
                    icon={
                      openStates[data.STNAMEBR] ? (
                        <ExpandMoreIcon />
                      ) : (
                        <ChevronRightIcon />
                      )
                    }
                    label={stateName}
                  />
                )}
              </TableCell>
              <TableCell
                style={{ textAlign: "left" }}
                onClick={openCloseState}
              ></TableCell>
              <TableCell
                {...tableCellProps(stateName)}
                style={
                  !nonMarketData
                    ? {
                        textDecoration: "underline",
                        color: "#0000FF",
                        fontWeight: "bold",
                      }
                    : {}
                }
              >
                {data.count?.toLocaleString()}
              </TableCell>
              <TableCell
                {...tableCellProps(stateName)}
                style={
                  !nonMarketData
                    ? {
                        textAlign: "right",
                        textDecoration: "underline",
                        color: "#0000FF",
                        fontWeight: "bold",
                      }
                    : { textAlign: "right" }
                }
              >
                {data.sum_DEPSUMBR?.toLocaleString()}
              </TableCell>
              <TableCell onClick={openCloseState}>
                {data.all_count?.toLocaleString()}
              </TableCell>
              <TableCell
                onClick={openCloseState}
                style={{ textAlign: "right" }}
              >
                {data.all_sum?.toLocaleString()}
              </TableCell>
              <TableCell onClick={openCloseState}>{data.mkt}</TableCell>
            </TableRow>
            {openStates[data.STNAMEBR] && (
              <MarketShareCountyList
                appState={appState}
                countyRows={data.counties}
                stname={stateName}
              />
            )}
          </React.Fragment>
        );
      })
    : [
        <tr key="no-results">
          <td colSpan={7}>
            Double check your search criteria for errors and try your search
            again.
          </td>
        </tr>,
      ];

  const headerStyle = (
    bgColor: string,
    align: string,
    textIndent?: string,
  ): KeyValue => {
    return {
      textAlign: align,
      backgroundColor: bgColor,
      color: textHeaderColor,
      textIndent: textIndent,
    };
  };
  const footer =
    Object.keys(footerData).length &&
    !(appState.filters.stalpbr && appState.filters.stalpbr !== "ALL"
      ? appState.filters.stalpbr
      : "") ? (
      <TableRow className={classes.footer}>
        <TableCell style={headerStyle(primaryHeaderColor, "left", "20px")}>
          National Totals
        </TableCell>
        <TableCell style={headerStyle(primaryHeaderColor, "")}></TableCell>
        <TableCell style={headerStyle(secondaryHeaderColor, "")}>
          {footerData.count?.toLocaleString()}
        </TableCell>
        <TableCell style={headerStyle(secondaryHeaderColor, "right")}>
          {footerData.sum_DEPSUMBR?.toLocaleString()}
        </TableCell>
        <TableCell style={headerStyle(additionalHeaderColor, "")}>
          {footerData.all_count?.toLocaleString()}
        </TableCell>
        <TableCell style={headerStyle(additionalHeaderColor, "right")}>
          {footerData.all_sum?.toLocaleString()}
        </TableCell>
        <TableCell style={headerStyle(primaryHeaderColor, "")}>
          {footerData.mkt}
        </TableCell>
      </TableRow>
    ) : (
      <></>
    );

  const tableHeaders: TableHeader[][] = [
    [
      {
        title: " ",
        styles: headerStyle(primaryHeaderColor, ""),
        colSpan: 2,
      },
      {
        title: appState.ui.institutionName,
        styles: headerStyle(secondaryHeaderColor, ""),
        colSpan: 2,
      },
      {
        title: "All FDIC-Insured Institutions",
        styles: headerStyle(additionalHeaderColor, ""),
        colSpan: 2,
      },
      {
        title: " ",
        styles: headerStyle(primaryHeaderColor, ""),
        colSpan: 2,
      },
    ],
    (() => {
      const headerValues = ["Office Count", "Total Deposits (Thousands USD)"];
      const headerValuesAsterisk = [
        "Office Count",
        "Total Combined Deposits* (Thousands USD)",
      ];
      const showAsterisks = appState.filters.bhcRSSD;

      const openCloseButton =
        Object.keys(openStates).length > 1 ? (
          <Button
            className={classes.openCloseAll}
            onClick={() => {
              Object.values(openStates).every((val) => val)
                ? setOpenStates(
                    reduceObj(
                      Object.keys(openStates).map((val) => {
                        return { key: val, value: false };
                      }),
                    ),
                  )
                : setOpenStates(
                    reduceObj(
                      Object.keys(openStates).map((val) => {
                        return { key: val, value: true };
                      }),
                    ),
                  );
            }}
          >
            {Object.values(openStates).every((val) => val)
              ? "Close All"
              : "Open All"}
          </Button>
        ) : singleState ? (
          "FIPS Code"
        ) : (
          ""
        );
      const stateCountyHeader: TableHeader[] = [
        {
          title: "State / County",
          styles: headerStyle(primaryHeaderColor, "left", "20px"),
          colSpan: 1,
          apiField: "",
        },

        {
          title: openCloseButton,
          styles: headerStyle(primaryHeaderColor, ""),
          colSpan: 1,
          apiField: "",
        },
      ];
      const institutionHeader: TableHeader[] = (
        showAsterisks ? headerValuesAsterisk : headerValues
      ).map((header: string) => {
        return {
          title: header,
          styles: headerStyle(secondaryHeaderColor, ""),
          colSpan: 1,
          apiField: "",
        };
      });

      const allInstitutionsHeader: TableHeader[] = headerValues.map(
        (header: string) => {
          return {
            title: header,
            styles: headerStyle(additionalHeaderColor, ""),
            colSpan: 1,
            apiField: "",
          };
        },
      );

      const marketShare: TableHeader[] = [
        {
          title: "Market Share (%)",
          styles: headerStyle(primaryHeaderColor, ""),
          colSpan: 1,
          apiField: "",
        },
      ];

      return [
        ...stateCountyHeader,
        ...institutionHeader,
        ...allInstitutionsHeader,
        ...marketShare,
      ];
    })(),
  ];

  const apiData: apiDownloadProps[] = [
    {
      appState,
      fileName: `${fileNameFormat(
        `Market Presence Results ${appName(
          appState.filters.stalpbr && appState.filters.stalpbr !== "ALL"
            ? appState.filters.stalpbr
            : "",
          appState.ui.displayResults,
        )}`,
      )}.csv`,
      apiPath: "sod",
      text: "Download Results Data - formatted",
    },
  ];

  const download = dataExistsCheck && (
    <ApiDataDownload
      app="SOD"
      apiData={apiData}
      downloadData={[
        ...formattedDataForDownload,
        {
          "Reporting Period": `6/30/${appState.filters.lastYear}`,
          ...downloadCertOrRSSD,
          "State": "National Totals",
          "County": "-",
          "Market Office Count": footerData.count?.toLocaleString(),
          "Market Total Deposits (Thousands USD)":
            footerData.sum_DEPSUMBR?.toLocaleString(),
          "All Office Count": footerData.all_count?.toLocaleString(),
          "All Total Deposits (Thousands USD)":
            footerData.all_sum?.toLocaleString(),
          "Market Share": footerData.mkt,
        },
      ].flat()}
    />
  );

  const headingTitle = (
    <>
      <Typography
        sx={{
          color: (theme) => theme.palette.primary.main,
          width: 700,
          minWidth: 700,
          fontSize: 26,
          fontWeight: "bold",
          whiteSpace: "nowrap",
          paddingBottom: "0.5rem",
        }}
      >
        Market Presence Results -{" "}
        {appName(appState.filters.stalpbr, appState.ui.displayResults)}
      </Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "end",
          justifyContent: "space-between",
        }}
      >
        <Typography
          sx={{
            color: (theme) => theme.palette.primary.main,
            width: 700,
            minWidth: 700,
            fontSize: 18,
            fontWeight: "bold",
            whiteSpace: "nowrap",
            paddingBottom: "1rem",
          }}
        >
          {!appState.ui.institutionName && appState.filters.cert
            ? "No Results"
            : `${
                appState.filters.bhcRSSD
                  ? "Combined Insured Subsidiaries of "
                  : ""
              }${appState.ui.institutionName}`}
        </Typography>
        {download}
      </Box>
    </>
  );

  return (
    <Root>
      {branchOfficeModal}
      {headingTitle}
      <Divider />
      {searchPills}
      {moreInformation}
      <MultiHeaderTable
        useStickyHeader
        tableBody={
          <>
            {loading ? (
              <LoadingIndicator
                text="Loading"
                spinnerSize={32}
                textSx={{
                  fontSize: 20,
                  width: "100%",
                  textAlign: "center",
                }}
                spinnerSx={{
                  marginTop: 24,
                  width: "100%",
                  textAlign: "center",
                }}
                table
              />
            ) : (
              <>
                {table}
                {tableData.length > 1 && footer}
              </>
            )}
          </>
        }
        tableHeaders={tableHeaders}
      />
    </Root>
  );
}
